import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { MessageCircle, X, Bot, Phone, Mail, HelpCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';

function StickyHelpButton() {
  const [isOpen, setIsOpen] = useState(false);

  const options = [
    {
      id: 'ai-assistant',
      icon: <Bot className="h-5 w-5" />,
      label: "AI Assistant",
      description: "Get instant help",
      link: "/ai-assistant",
      color: "bg-[#006847]"
    },
    {
      id: 'call-us',
      icon: <Phone className="h-5 w-5" />,
      label: "Call Us",
      description: "(985) 351-8873",
      link: "tel:+19853518873",
      external: true,
      color: "bg-blue-500"
    },
    {
      id: 'email-support',
      icon: <Mail className="h-5 w-5" />,
      label: "Email Support",
      description: "info@dowurktoday.org",
      link: "mailto:info@dowurktoday.org",
      external: true,
      color: "bg-purple-500"
    },
    {
      id: 'resources',
      icon: <HelpCircle className="h-5 w-5" />,
      label: "Resources",
      description: "Guides & tutorials",
      link: "/resources",
      color: "bg-orange-500"
    }
  ];

  return (
    <div className="fixed bottom-[76px] md:bottom-6 right-4 md:right-6 z-50" data-testid="sticky-help-button">
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.9 }}
            transition={{ duration: 0.2 }}
            className="absolute bottom-16 right-0 w-64 bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden mb-2"
          >
            <div className="bg-gradient-to-r from-[#006847] to-[#A4D65E] p-4 text-white">
              <h3 className="font-bold text-lg">Need Help?</h3>
              <p className="text-sm opacity-90">Choose how you'd like to connect</p>
            </div>
            <div className="p-2">
              {options.map((option) => (
                option.external ? (
                  <a
                    key={option.id}
                    href={option.link}
                    className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors"
                    onClick={() => setIsOpen(false)}
                  >
                    <div className={`${option.color} text-white p-2 rounded-lg`}>
                      {option.icon}
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{option.label}</p>
                      <p className="text-xs text-gray-500">{option.description}</p>
                    </div>
                  </a>
                ) : (
                  <Link
                    key={option.id}
                    to={option.link}
                    className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors"
                    onClick={() => setIsOpen(false)}
                  >
                    <div className={`${option.color} text-white p-2 rounded-lg`}>
                      {option.icon}
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{option.label}</p>
                      <p className="text-xs text-gray-500">{option.description}</p>
                    </div>
                  </Link>
                )
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main Button */}
      <motion.button
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => setIsOpen(!isOpen)}
        className={`w-14 h-14 rounded-full shadow-lg flex items-center justify-center transition-colors ${
          isOpen 
            ? 'bg-gray-700 text-white' 
            : 'bg-gradient-to-r from-[#006847] to-[#A4D65E] text-white'
        }`}
      >
        <AnimatePresence mode="wait">
          {isOpen ? (
            <motion.div
              key="close"
              initial={{ rotate: -90, opacity: 0 }}
              animate={{ rotate: 0, opacity: 1 }}
              exit={{ rotate: 90, opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              <X className="h-6 w-6" />
            </motion.div>
          ) : (
            <motion.div
              key="help"
              initial={{ rotate: 90, opacity: 0 }}
              animate={{ rotate: 0, opacity: 1 }}
              exit={{ rotate: -90, opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              <MessageCircle className="h-6 w-6" />
            </motion.div>
          )}
        </AnimatePresence>
      </motion.button>

      {/* Pulse Animation */}
      {!isOpen && (
        <span className="absolute -top-1 -right-1 flex h-4 w-4">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#A4D65E] opacity-75"></span>
          <span className="relative inline-flex rounded-full h-4 w-4 bg-[#A4D65E]"></span>
        </span>
      )}
    </div>
  );
}

export default StickyHelpButton;
